import { useEffect, useState } from 'react';
import { useData } from '@/context/DataContext';
import { X, ChevronLeft, ChevronRight } from 'lucide-react';

export default function PracticePage() {
  const { fetchContent, getContentBySection, gallery, fetchGallery, loading } = useData();
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [currentImageIndex, setCurrentImageIndex] = useState(0);

  useEffect(() => {
    fetchContent('practice');
    fetchGallery();
  }, [fetchContent, fetchGallery]);

  const introContent = getContentBySection('practice', 'intro');

  const openLightbox = (index) => {
    setCurrentImageIndex(index);
    setLightboxOpen(true);
  };

  const closeLightbox = () => {
    setLightboxOpen(false);
  };

  const goToPrevious = (e) => {
    e.stopPropagation();
    setCurrentImageIndex((prev) => (prev === 0 ? gallery.length - 1 : prev - 1));
  };

  const goToNext = (e) => {
    e.stopPropagation();
    setCurrentImageIndex((prev) => (prev === gallery.length - 1 ? 0 : prev + 1));
  };

  // Handle keyboard navigation
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (!lightboxOpen) return;
      if (e.key === 'Escape') closeLightbox();
      if (e.key === 'ArrowLeft') setCurrentImageIndex((prev) => (prev === 0 ? gallery.length - 1 : prev - 1));
      if (e.key === 'ArrowRight') setCurrentImageIndex((prev) => (prev === gallery.length - 1 ? 0 : prev + 1));
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [lightboxOpen, gallery.length]);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="spinner" />
      </div>
    );
  }

  return (
    <div className="bg-white" data-testid="practice-page">
      <div className="max-w-4xl mx-auto px-4 py-12">
        <h1 className="text-3xl font-normal text-[#2c3e50] mb-2">
          Werfen Sie einen Blick in meine Praxis!
        </h1>
        <hr className="mb-8 border-gray-300" />

        {/* Two column info section like original */}
        <div className="grid md:grid-cols-2 gap-8 mb-10">
          <div className="text-[#2c3e50]">
            <p>
              Rollstuhlgerechte Praxis / Rollstuhlgerechte Toilette /<br />
              Fahrstuhl vorhanden
            </p>
          </div>
          <div className="text-[#2c3e50]">
            <p>
              Unsere Therapiestunden finden in ruhiger und angenehmer Atmosphäre in
              den Räumlichkeiten meiner Psychotherapeutischen Praxis statt.
            </p>
          </div>
        </div>

        {/* Gallery Grid - 4 columns like original */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
          {gallery.map((image, index) => (
            <div
              key={image.id}
              className="overflow-hidden cursor-pointer hover-zoom"
              onClick={() => openLightbox(index)}
            >
              <img
                src={image.url}
                alt={image.alt}
                className="w-full h-32 md:h-40 object-cover transition-transform duration-300 hover:scale-110"
                loading="lazy"
              />
            </div>
          ))}
        </div>

        {gallery.length === 0 && (
          <div className="text-center py-12 text-[#7f8c8d]">
            Bilder folgen ...
          </div>
        )}
      </div>

      {/* Lightbox Modal */}
      {lightboxOpen && gallery.length > 0 && (
        <div
          className="fixed inset-0 bg-black/90 z-50 flex items-center justify-center"
          onClick={closeLightbox}
        >
          {/* Close button */}
          <button
            onClick={closeLightbox}
            className="absolute top-4 right-4 text-white hover:text-gray-300 transition-colors z-10"
            aria-label="Schliessen"
          >
            <X className="w-8 h-8" />
          </button>

          {/* Previous button */}
          {gallery.length > 1 && (
            <button
              onClick={goToPrevious}
              className="absolute left-4 text-white hover:text-gray-300 transition-colors z-10 p-2"
              aria-label="Vorheriges Bild"
            >
              <ChevronLeft className="w-10 h-10" />
            </button>
          )}

          {/* Image container */}
          <div
            className="max-w-[90vw] max-h-[90vh] flex items-center justify-center"
            onClick={(e) => e.stopPropagation()}
          >
            <img
              src={gallery[currentImageIndex].url}
              alt={gallery[currentImageIndex].alt}
              className="max-w-full max-h-[85vh] object-contain"
            />
          </div>

          {/* Next button */}
          {gallery.length > 1 && (
            <button
              onClick={goToNext}
              className="absolute right-4 text-white hover:text-gray-300 transition-colors z-10 p-2"
              aria-label="Naechstes Bild"
            >
              <ChevronRight className="w-10 h-10" />
            </button>
          )}

          {/* Image counter */}
          <div className="absolute bottom-4 left-1/2 -translate-x-1/2 text-white text-sm">
            {currentImageIndex + 1} / {gallery.length}
          </div>

          {/* Image description */}
          {gallery[currentImageIndex].alt && (
            <div className="absolute bottom-10 left-1/2 -translate-x-1/2 text-white text-center max-w-md px-4">
              {gallery[currentImageIndex].alt}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
